import React from "react";
import { ownerApi } from "../../../../api/owner";
import { ownerFinanceStatsApi } from "../../../../api/ownerFinanceStats";
import { createOwnerJarvisCapabilities } from "./ownerJarvisCapabilities";
import { createOwnerJarvisCapabilityRegistry } from "./ownerJarvisCapabilityRegistry";
import { createOwnerJarvisSnapshotService } from "./ownerJarvisSnapshotService";
import { createOwnerJarvisMutationAwareApi } from "./ownerJarvisApi";
import { createOwnerJarvisBriefing } from "./ownerJarvisUtils";

export function useOwnerJarvisRuntime({ navigate }) {
  const financeProjectFlowRef = React.useRef(null);
  const dayFlowRef = React.useRef(null);
  const snapshotService = React.useMemo(() => createOwnerJarvisSnapshotService({ ownerApi }), []);

  React.useEffect(() => {
    snapshotService.start();
    snapshotService.refresh().catch(() => undefined);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") snapshotService.refresh().catch(() => undefined);
    }, snapshotService.ttlMs);
    return () => {
      window.clearInterval(timer);
      snapshotService.destroy();
    };
  }, [snapshotService]);

  const jarvisOwnerApi = React.useMemo(() => createOwnerJarvisMutationAwareApi({ ownerApi, snapshotService }), [snapshotService]);

  const capabilityRegistry = React.useMemo(() => createOwnerJarvisCapabilityRegistry(createOwnerJarvisCapabilities({
    ownerApi: jarvisOwnerApi,
    ownerFinanceStatsApi,
    navigate,
    flowRef: financeProjectFlowRef,
    dayFlowRef,
    snapshotService,
  }), { snapshotService }), [jarvisOwnerApi, navigate, snapshotService]);

  const actions = React.useMemo(() => capabilityRegistry.voiceActions(), [capabilityRegistry]);
  const introMessage = React.useMemo(() => createOwnerJarvisBriefing(), []);
  const onUnhandledUtterance = React.useCallback((input) => capabilityRegistry.dispatch(input), [capabilityRegistry]);
  const hasPriorityConversation = React.useCallback(() => (
    Boolean(financeProjectFlowRef.current?.stage)
    || Boolean(dayFlowRef.current?.kind)
    || Boolean(capabilityRegistry.hasPendingInteraction?.())
  ), [capabilityRegistry]);

  const resetConversationState = React.useCallback(() => {
    financeProjectFlowRef.current = null;
    dayFlowRef.current = null;
    try { window.sessionStorage.removeItem("wp-owner-day-pending-selection-until"); } catch { /* optional */ }
    capabilityRegistry.resetPendingInteractions?.();
  }, [capabilityRegistry]);

  return {
    actions,
    introMessage,
    onUnhandledUtterance,
    hasPriorityConversation,
    resetConversationState,
    capabilities: capabilityRegistry.describe(),
    loadNavigationLearning: ownerApi.navigationLearning,
    recordNavigationLearning: ownerApi.recordNavigationLearning,
  };
}
